package ui.campaign

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Filter
import compose.assets.svg.Plus
import kotlinx.coroutines.launch
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.layer_top_listing__search
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_CampaignEditorUI
import tk.mallumo.utils.DataState
import tools.SearchWrapper
import tools.findRoot
import tools.rememberLazyListState
import ui.campaign.detailv2.editor.CampaignEditorArgs
import ui.widget.*

data class CampaignListingArgs(var companyId: Long = -1)

@ComposableNavNode(CampaignListingArgs::class)
@Composable
fun CampaignListingUI() {
    with(CampaignListingScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> {
                val filterState = rememberDrawerState(DrawerValue.Closed)
                CampaignListingFilter(filterState) {
                    Content(state.nnvl, filterState)
                }
            }
        }
    }
}


@OptIn(ExperimentalFoundationApi::class)
@Composable
fun CampaignListingScope.Content(nnvl: CampaignListingScope.Wrapper, filterState: DrawerState) {
    val scope = rememberCoroutineScope()
    val stateHorizontal = rememberScrollState(0)
    val itemMeasurer = CampaignListingScope.headerSpecs.rememberMeasurer()
    val tooltipImageSize = if (LocalDisplayProvider.current.isMobile) 64F else 128F

    Column(
        Modifier.fillMaxSize()
            .padding(top = 8.dp)
            .padding(start = 8.dp)
    ) {
        Row {
            HomeSearchUI(
                filed = vm.searchFiled,
                label = Res.string.layer_top_listing__search,
                modifier = Modifier.weight(1F)
            )
            if (display.resolution.width < 1800) {
                Space(16.dp)
                Icon(
                    imageVector = Svg.Filter,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onPrimary,
                    modifier = Modifier.align(Alignment.CenterVertically)
                        .background(MaterialTheme.colorScheme.primary, CircleShape)
                        .clip(CircleShape)
                        .clickable {
                            scope.launch {
                                if (filterState.isOpen) filterState.close()
                                else filterState.open()
                            }
                        }.padding(8.dp)
                )
            }
        }
        Space(8.dp)
        val scrollState = nav.rememberLazyListState("campaign-${args.companyId}")
        Box(Modifier.fillMaxSize()) {
            Box(
                Modifier.fillMaxSize()
                    .tableMeasurer(itemMeasurer)
                    .horizontalScroll(stateHorizontal)
            ) {
                LazyColumn(
                    state = scrollState,
                    contentPadding = PaddingValues(end = 8.dp),
                    modifier = Modifier.fillMaxHeight()
                        .clip(CardDefaults.shape)
                        .width(itemMeasurer.rowWidth)
                ) {
                    stickyHeader {
                        ListingHeader(CampaignListingScope.headerSpecs)
                    }

                    itemsIndexed(
                        SearchWrapper.use(
                            fields = arrayOf(
                                Io.ListingCampaign.Item::id,
                                Io.ListingCampaign.Item::idHex,
                                Io.ListingCampaign.Item::name
                            ),
                            items = nnvl.state,
                            query = vm.searchFiled,
                            sort = compareByDescending {
                                if (it.data.id < 1000) Long.MAX_VALUE - it.data.id else it.data.id
                            }
                        )) { _, item ->
                        ListingDivider()
                        CampaignListingTooltip(item, itemMeasurer, tooltipImageSize) {
                            ListingItem(item, CampaignListingScope.headerSpecs) {
                                action(
                                    CampaignListingVM.Action.OpenDetail(
                                        companyId = item.data.companyId,
                                        campaignGroupId = item.data.groupId
                                    )
                                )
                            }
                        }
                    }
                    ListingFooter()
                }
            }
            HorizontalScrollbar(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .fillMaxWidth(),
                adapter = rememberScrollbarAdapter(stateHorizontal),
            )

            AddButton(
                modifier = Modifier.align(Alignment.BottomEnd)
                    .padding(16.dp)
            )
        }
    }
}

@Composable
private fun CampaignListingScope.AddButton(
    modifier: Modifier,
) {
    if (vm.canAdd) {
        FloatingActionButton(
            modifier = modifier,
            onClick = {
                if (vm.canCreateCampaign()) {
                    nav.findRoot().navTo_CampaignEditorUI(
                        CampaignEditorArgs(companyId = args.companyId)
                    )
                }
            }
        ) {
            Icon(Svg.Plus, "Create new campaign")
        }
    }
}